import React from "react";
import { Box, Paper, Stack, Typography, Skeleton, alpha } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

/**
 * Tarjeta de métrica con indicador de tendencia - Diseño mejorado
 */
export default function MetricCard({
  title,
  value,
  subtitle,
  trend,
  trendLabel = "vs last month",
  trendInverted = false,
  icon,
  color = "primary",
  loading = false,
  onClick,
}) {
  const getTrendColor = () => {
    if (trend === 0 || trend === undefined || trend === null) return "text.secondary";
    const isPositive = trend > 0;
    const isGood = trendInverted ? !isPositive : isPositive;
    return isGood ? "success.main" : "error.main";
  };

  const TrendIcon = () => {
    if (trend === 0 || trend === undefined || trend === null) return <TrendingFlatIcon fontSize="small" />;
    return trend > 0 ? (
      <TrendingUpIcon fontSize="small" />
    ) : (
      <TrendingDownIcon fontSize="small" />
    );
  };

  // Color mapping for gradients
  const colorGradients = {
    primary: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    secondary: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
    success: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    warning: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
    error: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
    info: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
  };

  const colorValues = {
    primary: "#3B82F6",
    secondary: "#8B5CF6",
    success: "#10B981",
    warning: "#F59E0B",
    error: "#EF4444",
    info: "#06B6D4",
  };

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 4,
          height: "100%",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" height={40} />
          <Skeleton variant="text" width="50%" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 4,
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": onClick
          ? {
              transform: "translateY(-4px)",
              boxShadow: `0 12px 24px ${alpha(colorValues[color] || colorValues.primary, 0.2)}`,
              borderColor: colorValues[color] || colorValues.primary,
              "& .metric-icon-box": {
                transform: "scale(1.1) rotate(5deg)",
              },
            }
          : {},
      }}
    >
      {/* Decorative background gradient */}
      <Box
        sx={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          background: colorGradients[color] || colorGradients.primary,
          opacity: 0.08,
          borderRadius: "50%",
          filter: "blur(30px)",
        }}
      />

      <Stack spacing={1.5} sx={{ position: "relative" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={600}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.8,
              fontSize: "0.7rem",
            }}
          >
            {title}
          </Typography>
          {icon && (
            <Box
              className="metric-icon-box"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2.5,
                background: colorGradients[color] || colorGradients.primary,
                color: "white",
                boxShadow: `0 4px 12px ${alpha(colorValues[color] || colorValues.primary, 0.35)}`,
                transition: "transform 0.3s ease",
              }}
            >
              {icon}
            </Box>
          )}
        </Stack>

        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            background: colorGradients[color] || colorGradients.primary,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
            {subtitle}
          </Typography>
        )}

        {trend !== undefined && trend !== null && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: (theme) => alpha(
                trend > 0
                  ? (trendInverted ? theme.palette.error.main : theme.palette.success.main)
                  : (trendInverted ? theme.palette.success.main : theme.palette.error.main),
                0.1
              ),
              alignSelf: "flex-start",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", color: getTrendColor() }}>
              <TrendIcon />
              <Typography variant="body2" fontWeight={700} sx={{ ml: 0.25 }}>
                {Math.abs(trend)}%
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {trendLabel}
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
